import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { Gallery, listGallery, paramJumpPath } from '../../api/gallery';
import { ApiError, absoluteURL } from '../../api/request';
import Pagination from '../../components/Pagination';
import GalleryFilterBar from './GalleryFilterBar';
import { useGalleryFilter } from './useGalleryFilter';
import { useScopedDictText } from '../../utils/dictOptions';
import { thumbUrl } from '../../utils/imgUrl';

const PAGE_SIZE = 24;

// 矩阵图 tab (2026-08-14 口径变更): 数据源 = 工艺参数行 support_matrix=1 的 paraMatrix 实时聚合视图.
// 只读展示; 编辑走参数行 (卡片点击直达来源参数行).
// 筛选栏与效果图 tab 共用 GalleryFilterBar / useGalleryFilter (含 URL 状态还原).

interface MatrixMeta {
    row?: number;
    col?: number;
    minPower?: number;
    maxPower?: number;
    minSpeed?: number;
    maxSpeed?: number;
    processingType?: string;
}

function parseMeta(json?: string): MatrixMeta | null {
    if (!json || json === 'null') return null;
    try {
        const o = JSON.parse(json) as Record<string, unknown>;
        if (!o || typeof o !== 'object') return null;
        return {
            row: o.row as number | undefined,
            col: o.col as number | undefined,
            minPower: o.minPower as number | undefined,
            maxPower: o.maxPower as number | undefined,
            minSpeed: o.minSpeed as number | undefined,
            maxSpeed: o.maxSpeed as number | undefined,
            processingType: o.processingType as string | undefined,
        };
    } catch {
        return null;
    }
}

function MatrixList() {
    const history = useHistory();
    const f = useGalleryFilter('/gallery/matrix');
    const { query, page, setPage } = f;
    const [list, setList] = useState<Gallery[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 加工类型 label (process_type, scope=archCode), 卡片上不露 code
    const processTypeText = useScopedDictText(
        'process_type',
        list.map((g) => g.archCode),
    );

    // query 是每次渲染新建的对象, 序列化后做依赖避免 load 每帧重建
    const queryJson = JSON.stringify(query);
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listGallery({
                ...(JSON.parse(queryJson) as typeof query),
                hasMatrix: 'yes', // 本 tab 只看矩阵图; 普通效果图在 /gallery
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [queryJson, page]);

    useEffect(() => {
        load();
    }, [load]);

    const metas = useMemo(
        () =>
            new Map(
                list.map((g) => [
                    `${g.archCode}-${g.id}`,
                    parseMeta(g.matrixJson),
                ]),
            ),
        [list],
    );
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            <div>
                <div className="text-base font-semibold">矩阵图</div>
                <div className="text-xs text-slate-500">
                    工艺参数上的矩阵图 (paraMatrix) 聚合视图 · 共 {total} 张 ·{' '}
                    <span className="text-slate-400">编辑请到来源参数行</span>
                </div>
            </div>

            <GalleryFilterBar f={f} total={total} />

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {loading ? (
                <div className="text-center text-slate-400 text-sm py-10">
                    加载中…
                </div>
            ) : list.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-10">
                    该筛选下暂无矩阵图
                </div>
            ) : (
                <div className="grid grid-cols-4 gap-4">
                    {list.map((g) => {
                        const meta = metas.get(`${g.archCode}-${g.id}`);
                        return (
                            <div
                                key={`${g.archCode}-${g.id}`}
                                className="card overflow-hidden cursor-pointer hover:border-brand-500 hover:shadow-sm"
                                onClick={() =>
                                    history.push(
                                        `/gallery/asset/${encodeURIComponent(g.archCode)}/${g.id}?kind=matrix`,
                                    )
                                }
                            >
                                {g.imageUrl ? (
                                    <img
                                        src={thumbUrl(absoluteURL(g.imageUrl))}
                                        alt={g.xcode || `matrix-${g.id}`}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            height: 120,
                                            objectFit: 'cover',
                                        }}
                                        loading="lazy"
                                    />
                                ) : (
                                    <div
                                        style={{ height: 120 }}
                                        className="bg-slate-100"
                                    />
                                )}
                                <div className="p-3 space-y-1">
                                    <div className="text-sm font-semibold font-mono">
                                        {g.xcode ? (
                                            <PermissionLink
                                                resource="material"
                                                to={`/materials/${encodeURIComponent(g.xcode)}`}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                title="查看材料详情"
                                                className="text-brand-600 hover:underline"
                                            >
                                                {g.xcode}
                                            </PermissionLink>
                                        ) : (
                                            '未关联材质'
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {g.deviceKey || '—'}
                                        {meta?.processingType
                                            ? ` · ${processTypeText(g.archCode, meta.processingType)}`
                                            : ''}
                                    </div>
                                    <div className="flex items-center gap-1.5 pt-0.5">
                                        {meta?.row && meta?.col && (
                                            <span className="tag bg-violet-50 text-violet-700 text-[10px]">
                                                {meta.row}×{meta.col}
                                            </span>
                                        )}
                                        {meta?.minPower !== undefined && (
                                            <span className="text-[11px] text-slate-400">
                                                功率 {meta.minPower}–
                                                {meta.maxPower}
                                            </span>
                                        )}
                                        {meta?.minSpeed !== undefined && (
                                            <span className="text-[11px] text-slate-400">
                                                速度 {meta.minSpeed}–
                                                {meta.maxSpeed}
                                            </span>
                                        )}
                                        <Link
                                            to={paramJumpPath(
                                                g,
                                                meta?.processingType,
                                            )}
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-[11px] text-brand-600 hover:underline ml-auto whitespace-nowrap"
                                        >
                                            工艺参数 →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 张</span>
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                />
            </div>
        </div>
    );
}

export default MatrixList;
